import {formatDate, formatNumber} from '@angular/common';
import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import {map} from 'rxjs/operators';
import * as echarts from 'echarts';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {countOf} from '@features/admin-panel/helpers/admin-dashboard-text';
import {adbAlpha, adbCategoryAxis, adbChartTheme, adbGrid, adbRedrawOnThemeChange, adbTooltip, adbValueAxis} from '@features/admin-panel/helpers/admin-dashboard-chart-theme';
import {adbChange} from '@features/admin-panel/helpers/admin-dashboard-format';
import {
  AdminDashboardQueryParams,
  AdminDashboardSignupsPerWeek,
  AdminDashboardWidgetState
} from '@features/admin-panel/models/admin-dashboard.models';
import {AdminDashboardReportBlock, AdminDashboardReportProvider} from '@features/admin-panel/models/admin-dashboard-report.models';
import {AdminDashboardReportRegistry} from '@features/admin-panel/services/admin-dashboard-report-registry.service';
import {AdminDashboardDrillService} from '../drill/admin-dashboard-drill.service';
import {adbDrillRowView} from '@features/admin-panel/helpers/admin-dashboard-drill';
import {AdminDashboardDrillView, AdminDashboardSignupsWeekDrill} from '@features/admin-panel/models/admin-dashboard-drill.models';

/**
 * The ECharts entry point behind a plain object, so a spec can stub it: the `echarts` module namespace is
 * read-only, and `spyOn(echarts, 'init')` throws.
 */
export const signupsPerWeekCharts = {
  init: (element: HTMLElement): echarts.ECharts => echarts.init(element)
};

const day = (iso: string): string => formatDate(iso, 'd MMM', 'en-US', 'UTC');

/**
 * Users & adoption "Sign-ups per week": users created per ISO week; the weeks inside the period are drawn strong.
 * Reloads whenever the dashboard's query params change.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-signups-per-week',
  templateUrl: './admin-dashboard-signups-per-week.component.html',
  styleUrls: ['./admin-dashboard-signups-per-week.component.scss']
})
export class AdminDashboardSignupsPerWeekComponent implements OnChanges, OnInit, OnDestroy, AdminDashboardReportProvider {
  /** Redraws with the new theme's colours when the painted theme flips (GEN-E-137-08). */
  private readonly themeRedraw = adbRedrawOnThemeChange(() => this.render());
  @Input() params: AdminDashboardQueryParams;
  /** "Evidence" in a week's drill: the dashboard opens that company's why panel (ITP-E-159-07). */
  @Output() companySelected = new EventEmitter<string>();
  /** What the card has to show, reported to the dashboard (ITP-E-173-01). */
  @Output() stateChange = new EventEmitter<AdminDashboardWidgetState>();
  widgetState: AdminDashboardWidgetState = 'loading';

  readonly failedTitle = 'Couldn\'t load sign-ups per week';

  data: AdminDashboardSignupsPerWeek | null = null;
  /** False until the first response, so the empty state never flashes. */
  loaded = false;
  /** The last load failed: the card says so instead of looking empty. */
  failed = false;

  private chartElement: HTMLDivElement;
  private chart: echarts.ECharts;
  private resizeObserver: ResizeObserver;
  private loadSubscription: Subscription;

  constructor(private dataService: AdminDashboardDataService, private reportRegistry: AdminDashboardReportRegistry, private host: ElementRef<HTMLElement>, private drill: AdminDashboardDrillService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['params']) {
      this.load();
    }
  }

  ngOnInit(): void {
    this.reportRegistry.register('users-adoption', this.host.nativeElement, this);
  }

  /** The chart container exists only while there are weeks to draw. */
  @ViewChild('chart') set chartContainer(container: ElementRef<HTMLDivElement> | undefined) {
    if (container?.nativeElement === this.chartElement) {
      return;
    }
    this.disposeChart();
    if (container) {
      this.chartElement = container.nativeElement;
      this.chart = signupsPerWeekCharts.init(this.chartElement);
      this.chart.on('click', (event: any) => this.openWeek(event?.dataIndex));
      this.resizeObserver = new ResizeObserver(() => this.chart?.resize());
      this.resizeObserver.observe(this.chartElement);
      this.render();
    }
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.disposeChart();
    this.reportRegistry.unregister(this);
  }

  /** The card for the PDF report, built from what it shows now; never reloads (ITP-E-146-09). */
  reportBlocks(): AdminDashboardReportBlock[] {
    if (!this.loaded) {
      return [];
    }
    if (this.failed) {
      return [{kind: 'empty', title: 'Sign-ups per week', text: this.failedTitle}];
    }
    if (!this.hasWeeks) {
      return [{kind: 'empty', title: 'Sign-ups per week', text: 'No sign-ups in this view'}];
    }
    return [{
      kind: 'chart',
      title: 'Sign-ups per week',
      subtitle: `${countOf(this.data.periodTotal, 'new user')} in the period. Weeks inside it are drawn strong.`,
      image: this.chart ? this.chart.getDataURL({type: 'png', pixelRatio: 2, backgroundColor: '#FFFFFF'}) : null,
      widthPx: this.chart?.getWidth() ?? 0,
      heightPx: this.chart?.getHeight() ?? 0
    }];
  }

  get hasWeeks(): boolean {
    return (this.data?.weeks?.length ?? 0) > 0;
  }

  /** "Week of 7 Sep: 12 new users<br/>+20% vs the week before" — the change only when the week before had sign-ups. */
  tooltipText(index: number): string {
    const week = this.data?.weeks?.[index];
    if (!week) {
      return '';
    }
    const line = `Week of ${day(week.weekStart)}: ${countOf(week.value, 'new user')}`;
    const before = this.data.weeks[index - 1];
    const change = before ? adbChange(week.value, before.value) : '';
    return change ? `${line}<br/>${change} vs the week before` : line;
  }

  /**
   * A week was clicked (OCA-E-159 DetailSections.tsx:98-114): the drill lists the companies whose users joined that
   * ISO week, most new users first. The title names the week's Sunday. Evidence there opens the why panel.
   */
  openWeek(index: number): void {
    const week = this.data?.weeks?.[index ?? -1];
    if (!week || !this.params) {
      return;
    }
    const params = this.params;
    const key = formatDate(week.weekStart, 'yyyy-MM-dd', 'en-US', 'UTC');
    const weekEnd = new Date(Date.parse(week.weekStart) + 6 * 24 * 60 * 60 * 1000);
    this.drill.open({
      title: `New users, week ending ${formatDate(weekEnd, 'd MMM yyyy', 'en-US', 'UTC')}`,
      columns: [{header: 'New users', align: 'end'}],
      load: () => this.dataService.getWithExtra<AdminDashboardSignupsWeekDrill>('AdminDashboardSignupsWeekDrillQuery', params, {WeekStart: key}).pipe(
        map((response): AdminDashboardDrillView => ({
          subtitle: `${countOf(response?.TotalUsers ?? 0, 'user')} joined ${countOf(response?.Total ?? 0, 'company', 'companies')}.`,
          rows: (response?.Rows ?? []).map(row => adbDrillRowView(row, [{text: formatNumber(row.newUsers ?? 0, 'en-US', '1.0-0')}])),
          total: response?.Total ?? 0
        }))
      )
    }).subscribe(result => {
      if (result?.action === 'evidence') {
        this.companySelected.emit(result.companyId);
      }
    });
  }

  private load(): void {
    this.loadSubscription?.unsubscribe();
    if (!this.params) {
      return;
    }
    // A reload keeps showing the last result until the new one arrives, so only the first load reports 'loading'.
    if (!this.loaded) {
      this.report('loading');
    }
    this.loadSubscription = this.dataService.getData<AdminDashboardSignupsPerWeek>(
      'AdminDashboardSignupsPerWeekQuery', this.params, 'SignupsPerWeek'
    ).subscribe({
      next: data => this.show(data ?? null, false),
      error: () => this.show(null, true)
    });
  }

  private show(data: AdminDashboardSignupsPerWeek | null, failed: boolean): void {
    this.data = data;
    this.failed = failed;
    this.loaded = true;
    this.render();
    this.report(failed ? 'failed' : this.hasWeeks ? 'content' : 'empty');
  }

  private report(state: AdminDashboardWidgetState): void {
    this.widgetState = state;
    this.stateChange.emit(state);
  }

  private disposeChart(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.chart?.dispose();
    this.chart = null;
    this.chartElement = null;
  }

  private render(): void {
    if (!this.chart || !this.data) {
      return;
    }
    const weeks = this.data.weeks ?? [];
    // OCA-E-159 signupsOption (charts.ts:198-223): the weeks inside the period in blue 2, the rest at 35 %, themed
    // axes, the tooltip on the card surface. Colours come from the card's --adb-* tokens.
    const t = adbChartTheme(this.host.nativeElement);
    const option = {
      textStyle: {fontFamily: t.font},
      grid: adbGrid({top: 12}),
      xAxis: adbCategoryAxis(t, weeks.map(w => day(w.weekStart)), {axisLabel: {color: t.ink2, fontSize: 10, interval: 'auto'}}),
      yAxis: adbValueAxis(t, (value: number) => formatNumber(value, 'en-US', '1.0-0'), {minInterval: 1}),
      tooltip: adbTooltip(t, {
        axisPointer: {type: 'shadow'},
        formatter: (points: any) => `${this.tooltipText((Array.isArray(points) ? points[0] : points)?.dataIndex ?? 0)}<br/><span style="color:${t.ink2}">Click to see the companies</span>`
      }),
      series: [{
        type: 'bar',
        barMaxWidth: 22,
        data: weeks.map(w => ({
          value: w.value,
          itemStyle: {color: w.inPeriod ? t.blue2 : adbAlpha(t.blue2, 0.35), borderRadius: [3, 3, 0, 0]}
        }))
      }]
    };
    this.chart.setOption(option, true);
  }
}
